









































































































































































import { Component, Vue, Watch } from 'vue-property-decorator';
import UButton from '@/ui-lib/button/Button.vue';
import { Tag, TagRecommendation, TrackTag } from '@/types';
import USelectField, {
  SelectMultipleValue,
  SelectOption
} from '@/ui-lib/select-field/SelectField.vue';
import { TagService } from '@/api';
import { tag as tagStore } from '@/store/modules/tag';

export type TrackTagsSelectModalParams = {
  selected?: TrackTag[];
  trackIds?: number[];
  // Off where the picked tags only narrow a search: a new tag has no tracks to find.
  allowCreate?: boolean;
  submitLabel?: string;
  onSelect?: (value: SelectMultipleValue) => Promise<void>;
};

// Пункт списка: обычный тег со счётчиком треков либо предложение завести новый.
// createName хранит исходный ввод — label у такого пункта это подпись строки.
// Тег, подошедший под запрос: rank — ступень близости, byName отличает
// совпадение по названию от алиасного, alias подписывается в строке — без него
// тег выглядит случайным: искали «поп», а в списке «Rock».
type TagMatch = {
  tag: Tag;
  rank: number;
  byName: boolean;
  alias?: string;
};

const NO_MATCH = 4;

// За сессию потеговки модалка открывается десятки раз, а справочник меняется
// редко — чаще раза в минуту перечитывать его целиком незачем.
const tagsRefreshIntervalMs = 60_000;
let tagsLoadedAt = 0;

type TagOption = SelectOption & {
  trackCount?: number;
  aliasHint?: string;
  isCreate?: boolean;
  createName?: string;
};

@Component({
  components: {
    USelectField,
    UButton
  }
})
export default class extends Vue {
  private selected: TrackTag[] = [];
  private onSelect: (value: SelectMultipleValue) => Promise<void> = async () => {
    //
  };

  private isLoading = false;
  private query = '';
  private value: SelectMultipleValue = null;
  private trackIds: number[] = [];
  private allowCreate = true;
  private submitLabel = 'Продолжить';
  private recommendations: TagRecommendation[] = [];
  private recommendAbort: AbortController | null = null;

  private get allTags(): Tag[] {
    return tagStore.context(this.$store).state.list;
  }

  private get valueList(): SelectOption[] {
    const value: SelectMultipleValue = this.value;

    if (Array.isArray(value)) {
      return value;
    }

    return value ? [value] : [];
  }

  private get matches(): TagMatch[] {
    const query = this.query.trim().toLowerCase();
    const chosen = new Set(this.valueList.map((v) => v.label.toLowerCase()));

    const rest = this.allTags.filter((tag) => !chosen.has(tag.name.toLowerCase()));

    if (!query) {
      return [...rest]
        .sort((a, b) => b.trackCount - a.trackCount)
        .map((tag) => ({ tag, rank: 0, byName: true }));
    }

    return rest
      .map((tag) => this.match(tag, query))
      .filter((match): match is TagMatch => !!match)
      .sort(
        (a, b) =>
          a.rank - b.rank ||
          Number(b.byName) - Number(a.byName) ||
          b.tag.trackCount - a.tag.trackCount
      );
  }

  // Близость строки к запросу: точное совпадение → начинается с запроса →
  // запрос начинает слово внутри → просто вхождение. Средняя ступень и держит
  // выдачу осмысленной: «Dance Pop» стоит выше «Kpopish», а «Rock» и «Funk»,
  // которые на «k» подтягиваются вхождением, уходят вниз.
  private closeness(text: string, query: string): number {
    const lower = text.toLowerCase();

    if (lower === query) {
      return 0;
    }

    if (lower.startsWith(query)) {
      return 1;
    }

    if (this.words(text).some((word) => word.startsWith(query))) {
      return 2;
    }

    if (lower.includes(query)) {
      return 3;
    }

    return NO_MATCH;
  }

  // Алиас считается той же близостью, но на ступень ниже названия: тег «Pop» с
  // алиасом ровно «поп» обязан обгонять «Тин-поп», где запрос лишь начинает
  // слово, а при равной ступени вперёд идёт совпадение по самому названию.
  private match(tag: Tag, query: string): TagMatch | null {
    const nameRank = this.closeness(tag.name, query);

    let alias: string | undefined;
    let aliasRank = NO_MATCH;

    (tag.search_aliases || []).forEach((candidate) => {
      const rank = this.closeness(candidate, query);

      if (rank < aliasRank) {
        aliasRank = rank;
        alias = candidate;
      }
    });

    const rank = Math.min(nameRank, aliasRank + 1);

    if (rank >= NO_MATCH) {
      return null;
    }

    // Подпись нужна не только когда имя не совпало вовсе: тег, поднятый алиасом
    // выше собственного совпадения по имени, иначе выглядит в выдаче случайным.
    const byAlias = aliasRank + 1 < nameRank;

    return { tag, rank, byName: nameRank < NO_MATCH, alias: byAlias ? alias : undefined };
  }

  private get options(): TagOption[] {
    const query = this.query.trim();
    const lower = query.toLowerCase();

    const list: TagOption[] = this.matches.map(({ tag, alias }) => ({
      label: tag.name,
      value: tag.id,
      trackCount: tag.trackCount,
      aliasHint: alias
    }));

    // Список отдаём целиком, без порции и виртуализации: справочник — сотни
    // тегов (163 на проде), а обрезка прятала бы нужный тег ниже видимых строк.
    // Создание — последней строкой намеренно: сверху стоит лучшее совпадение,
    // чтобы Enter добавлял существующий тег, а не плодил дубль вроде «pop»/«Pop».
    if (this.allowCreate && query && !this.nameTaken(lower)) {
      list.push({
        label: `Создать тег «${query}»`,
        value: `create:${lower}`,
        isCreate: true,
        createName: query
      });
    }

    return list;
  }

  // Границей слова считается и смена регистра: иначе «beat» не нашёл бы
  // «BreakBeat» ступенью выше, чем случайное вхождение в середину слова.
  private words(name: string): string[] {
    return name
      .replace(/([a-zа-яё])([A-ZА-ЯЁ])/g, '$1 $2')
      .toLowerCase()
      .split(/[^a-zа-яё0-9]+/)
      .filter(Boolean);
  }

  // Имя, занятое алиасом чужого тега, тоже считается занятым: «поп» ведёт к
  // тегу «Pop», и отдельный тег с таким именем — тот самый дубль по смыслу.
  private nameTaken(lowerName: string): boolean {
    const takenByTag = (tag: Tag): boolean =>
      tag.name.toLowerCase() === lowerName ||
      (tag.search_aliases || []).some((alias) => alias.toLowerCase() === lowerName);

    return (
      this.allTags.some(takenByTag) ||
      this.valueList.some((v) => v.label.toLowerCase() === lowerName)
    );
  }

  private trackCountText(count?: number): string {
    const total = count || 0;
    const word =
      total % 10 === 1 && total % 100 !== 11
        ? 'трек'
        : [2, 3, 4].includes(total % 10) && ![12, 13, 14].includes(total % 100)
          ? 'трека'
          : 'треков';

    return `${total.toLocaleString()} ${word}`;
  }

  // Рекомендации, которых ещё нет в выборе (по id или по имени — созданные теги
  // попадают в value строкой). Computed, чтобы клик «+» убирал тег из списка сам.
  private get visibleRecommendations(): TagRecommendation[] {
    return this.recommendations.filter(
      (tag) => !this.valueList.some((v) => v.value === tag.id || v.label === tag.name)
    );
  }

  // Пункт «создать» живёт в списке обычной опцией, но в выборе должен стать
  // именем тега: наружу уходит label, а подпись строки туда попадать не должна.
  @Watch('value')
  private valueChanged(val: SelectMultipleValue) {
    const list = Array.isArray(val) ? val : val ? [val] : [];

    if (!list.some((v) => (v as TagOption).isCreate)) {
      return;
    }

    this.value = list.map((v) => {
      const option = v as TagOption;

      if (!option.isCreate) {
        return v;
      }

      const name = option.createName || '';

      return { label: name, value: name };
    });

    this.query = '';
  }

  private addOption(option: SelectOption): void {
    this.value = [...this.valueList, option];
    this.query = '';

    const select = this.$refs.tags as USelectField | undefined;

    if (select) {
      select.clearSearch();
    }
  }

  private close(): void {
    this.$modal.hide('track-tags-select');
  }

  private addRecommendation(tag: TagRecommendation): void {
    this.addOption({ label: tag.name, value: tag.id });
  }

  private beforeOpen({ params }: { params: TrackTagsSelectModalParams }) {
    if (this.recommendAbort) {
      this.recommendAbort.abort();
    }

    this.isLoading = false;
    this.query = '';
    this.recommendations = [];
    this.selected = params?.selected || [];
    this.trackIds = params?.trackIds || [];
    this.allowCreate = params?.allowCreate ?? true;
    this.submitLabel = params?.submitLabel || 'Продолжить';
    this.value = null;
    this.onSelect =
      params?.onSelect ||
      (async () => {
        //
      });

    if (this.selected.length) {
      this.value = this.selected.map((tt) => ({
        label: tt.name,
        value: tt.id
      }));
    }

    this.loadTags();
    this.loadRecommendations();
  }

  // Справочник тегов уже загружен целиком при входе в кабинет — читаем его из
  // стора, а запрос шлём только чтобы подхватить теги, заведённые в другом окне.
  // Лоадер и тост про ошибку — только когда работать реально не с чем: фоновое
  // обновление не должно ни гасить оверлей уже идущего сохранения, ни ругаться.
  private async loadTags() {
    const isEmpty = !this.allTags.length;

    if (!isEmpty && Date.now() - tagsLoadedAt < tagsRefreshIntervalMs) {
      return;
    }

    if (isEmpty) {
      this.isLoading = true;
    }

    try {
      await tagStore.context(this.$store).actions.loadList();
      tagsLoadedAt = Date.now();
    } catch (e) {
      if (isEmpty) {
        this.$notify({ type: 'error', title: 'Ошибка', text: e?.message || 'Неизвестная ошибка' });
      }
    }

    if (isEmpty) {
      this.isLoading = false;
    }
  }

  private async loadRecommendations() {
    if (!this.trackIds.length) {
      return;
    }

    this.recommendAbort = new AbortController();

    try {
      this.recommendations = await TagService.recommend(
        { track_ids: this.trackIds },
        { signal: this.recommendAbort.signal }
      );
    } catch (e) {
      // Рекомендации необязательны (в т.ч. отменённый при переоткрытии запрос) —
      // молча игнорим, чтобы не мешать выбору тегов.
    }
  }

  private onSearch(val: string) {
    this.query = val;
  }

  private async select() {
    this.isLoading = true;

    try {
      const copiedValue = this.value
        ? Array.isArray(this.value)
          ? this.value.map((t) => ({ ...t }))
          : { ...this.value }
        : null;
      await this.onSelect(copiedValue);
      this.close();
    } catch (e) {
      this.$notify({ type: 'error', title: 'Ошибка', text: e?.message || 'Неизвестная ошибка' });
    }

    this.isLoading = false;
  }
}
